<script setup lang="ts">
import { computeIsSellerPov } from '@lib/shared';

import {
    apiTriggerInspectionExpiredBlockNotifications,
    BackendErrorCode,
    type BackendErrorEntity,
    DirectSaleComputedState,
    type DirectSaleItemEntity,
    DirectSaleState,
    type ErrorType,
    LeadIntent,
    NegotiationState,
    type OrderEntity,
    TriggerInspectionExpiredBlockNotificationsSaleType,
    useApiPlaceOrderDirectSale,
    useApiUpdateAccountPersonalData,
    useApiDirectSaleDetail,
} from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { isDirectSaleItemEntityWithPrice, isDirectSaleWithNegotiations } from '~/types/directSale.type';
import { NotificationDuration } from '~/types/notifications.type';

export type DsdpSubflowType = 'order' | 'negotiation';

interface Props {
    directSale: DirectSaleItemEntity;
}
// PROPS
const { directSale } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { notifyError, notifyGenericError, notifyBackendError } = useNotification();
const { logError } = useLogs();
const { data: session, isLoggedIn, isCustomerService } = useAuthUtils();
const { openAuthModal } = useModals();
const { hasRestrictedNotice, hasInspectionNotice, isBailNeeded, hasOrder, isSold, isSeller, hasInspectionExportBlock, inspectionExportBlockReason } =
    useDirectSaleStatus(toRef(() => directSale));
const { isToBeScraped } = useSaleType(directSale.type);
const { mutateAsync: mutatePlaceOrder, isPending: isOrderPending } = useApiPlaceOrderDirectSale();
const { makeProposal } = useDirectSaleNegotiation(() => directSale);
const route = useRoute();
const localePath = useLocalePath();
const { updateTrackingCompanyMetrics, trackDsNegotiationStartNegotiation, trackDsdpOrderModalClose, trackDirectSaleOfferPlaced } = useTracking();
const inspectionExpiredModalState = useModalState<boolean>();
const bailModalState = useModalState<boolean>();
const orderModalState = useModalState<boolean>();
const { captureLeadContacts, closeLeadModal } = useSdpLead();
const { mutateAsync: mutatePersonalData } = useApiUpdateAccountPersonalData();
const { isOnboardingInProgress } = useOnboarding();
const { refetch } = useApiDirectSaleDetail(directSale.id);

// DATA
const subflowType = ref<DsdpSubflowType>('order');
const currentOrderId = ref<string | null>(null);
const currentNegotiationId = ref<string | null>(null);
const offerAmount = ref<number | undefined>(directSale.price);
// Track whether the user has modified the offer via the stepper, so we don't
// silently overwrite their chosen value if directSale.price changes at runtime.
const hasUserModifiedOffer = ref<boolean>(false);
const isSellerSalesRep = computed(() => !!directSale.userMeta?.isSalesRep && !!directSale.userMeta?.isSellerSalesRep);
const isSellerPov = computed(() =>
    computeIsSellerPov({ isSeller: isSeller.value, isCustomerService: isCustomerService.value, isAssignedSalesRep: isSellerSalesRep.value })
);
// Reducing the price acts for the sale's company, which only the backend can tell: the side above is who is looking.
const canReducePrice = computed(() => canReduceSalePrice(directSale, isCustomerService.value));
const makeOfferFlowInProgress = ref<boolean>(false);
const loginFlowInProgress = ref<boolean>(false);
const userMeta = computed(() => directSale.userMeta);
const isLoadingModalOpen = ref(false);
const loadingModalTitle = ref('');
const isInspectionExportBlockModalOpen = ref(false);
const shouldShowInspectionExpiredModal = computed(
    () =>
        !!userMeta.value?.inspectionExpiredExportRestrictions &&
        (isBailNeeded.value || (userMeta.value.isWhitelisted && !directSale?.negotiations?.length))
);

const hasMakeOffer = computed(() => {
    if (isLoggedIn.value && isSellerPov.value) return false;

    if (isDirectSaleWithNegotiations(directSale)) {
        const lastNegotiation = directSale.negotiations.toSorted((a, b) => {
            const dateA = Date.parse(a.createdAt);
            const dateB = Date.parse(b.createdAt);

            return dateB - dateA;
        })[0];
        if (lastNegotiation.state === NegotiationState.Open) return false;
    }

    return directSale.computedState === DirectSaleComputedState.NoActiveOrder;
});
const hasGoToNegotiation = computed(() => isLoggedIn.value && isDirectSaleWithNegotiations(directSale));
const hasPriceReduction = computed(
    () => isLoggedIn.value && canReducePrice.value && directSale.state === DirectSaleState.Open && !isToBeScraped.value
);
const showSection = computed(
    () => hasRestrictedNotice.value || hasInspectionNotice.value || hasMakeOffer.value || hasGoToNegotiation.value || hasPriceReduction.value
);

// HANDLERS
const loginFlow = async (): Promise<void> => {
    loginFlowInProgress.value = true;

    try {
        const customerLeadContacts = await captureLeadContacts({
            sale: directSale,
            intentType: LeadIntent.DirectSaleBuy,
        });

        if (!customerLeadContacts) return;

        if (!isLoggedIn.value || isOnboardingInProgress.value) {
            if (!(await openAuthModal({ email: customerLeadContacts.email, phone: customerLeadContacts.phone, action: 'MAKE_OFFER' }))) return;

            await refetch();
        }

        if (!session.value?.user.phone) await mutatePersonalData({ data: { phone: customerLeadContacts.phone } });
    } catch (error) {
        logError(`Error in DSDP login`, { directSale: directSale.id, userId: session.value?.user.id, error });
    } finally {
        loginFlowInProgress.value = false;
    }
};

const placeOrderApi = (): Promise<OrderEntity> => {
    const apiPromise = mutatePlaceOrder(
        { data: { directSaleId: directSale.id } },
        {
            onError: (error: ErrorType<BackendErrorEntity>, variables: Object): void => {
                logError('Error placing DirectSale order', { apiError: error.data, apiData: variables });
            },
        }
    );

    apiPromise.catch((apiError: ErrorType<BackendErrorEntity>) => {
        if (!isBackendError(apiError.data)) {
            notifyGenericError();

            return;
        }

        const { error } = apiError.data;

        if ([BackendErrorCode.AuthTokenExpired, BackendErrorCode.AuthTokenNotValid].includes(error.code)) {
            // Errors already handled elsewhere
            return;
        }

        // Safety net for a stale userMeta race: the preventive block intercept was bypassed and the order was
        // actually attempted (which already fired the email server-side). Show the specific error toast.
        if (
            [BackendErrorCode.CantPerformInspectionExpiredNotExportable, BackendErrorCode.CantPerformInspectionExpiringSoonNotExportable].includes(
                error.code
            )
        ) {
            notifyBackendError({ error });

            return;
        }

        notifyError({
            title: tt(`sdp.DirectSale.order.orderError.title`),
            description: tt(`sdp.DirectSale.order.orderError.description`),
            duration: NotificationDuration.NoTimeout,
        });
    });

    return apiPromise;
};

// Resolves on success, throws on cancellation or error. The caller catches both
// uniformly to stop the loading state — no need to distinguish cancel from error.
const placeOrderSubFlow = async (): Promise<void> => {
    loadingModalTitle.value = tt('sdp.DirectSale.orderInProgressModal.title');

    if (isBailNeeded.value) {
        const order = await placeOrderApi();
        currentOrderId.value = order.id;
        if (!(await bailModalState.open())) throw undefined;
    } else {
        if (!(await orderModalState.open())) {
            trackDsdpOrderModalClose();
            throw undefined;
        }

        isLoadingModalOpen.value = true;
        await placeOrderApi();
    }

    isLoadingModalOpen.value = true;
    await until(isSold).toBe(true);
    updateTrackingCompanyMetrics();
};

// Resolves on success, throws on cancellation or error. The caller catches both
// uniformly to stop the loading state — no need to distinguish cancel from error.
const startNegotiationSubFlow = async (offerAmount: number): Promise<void> => {
    loadingModalTitle.value = tt('directSaleNegotiation.firstOfferInProgressModal.title');

    currentNegotiationId.value = await makeProposal(offerAmount);

    if (isBailNeeded.value) {
        if (!(await bailModalState.open())) throw undefined;
    }

    isLoadingModalOpen.value = true;
    await until(hasGoToNegotiation).toBe(true);
    trackDirectSaleOfferPlaced();
    navigateTo(localePath({ name: ROUTE_NAMES.DIRECT_SALE_DETAIL_NEGOTIATION, params: route.params }));
};

const makeOfferFlow = async (): Promise<void> => {
    if (offerAmount.value === undefined) return;
    if (!isDirectSaleItemEntityWithPrice(directSale)) return notifyGenericError();
    if (!isLoggedIn.value) return notifyGenericError();

    makeOfferFlowInProgress.value = true;
    trackDsNegotiationStartNegotiation();

    try {
        const isPlaceOrderSubFlow = offerAmount.value >= directSale.buyNowPrice;
        subflowType.value = isPlaceOrderSubFlow ? 'order' : 'negotiation';

        const customerLeadContacts = await captureLeadContacts({
            sale: directSale,
            intentType: subflowType.value === 'order' ? LeadIntent.DirectSaleBuy : LeadIntent.DirectSaleProposal,
        });

        if (!customerLeadContacts) return;

        if (isOnboardingInProgress.value) {
            if (!(await openAuthModal({ email: customerLeadContacts.email, phone: customerLeadContacts.phone, action: 'MAKE_OFFER' }))) return;

            await refetch();
        }

        if (hasInspectionExportBlock.value) {
            // Notify seller + sales reps of the blocked attempt (fire-and-forget; the outcome is irrelevant to the flow). Covers buy-now and offer.
            apiTriggerInspectionExpiredBlockNotifications({
                saleType: TriggerInspectionExpiredBlockNotificationsSaleType.DirectSale,
                saleId: directSale.id,
            }).catch(() => undefined);
            isInspectionExportBlockModalOpen.value = true;

            return;
        }

        if (hasRestrictedNotice.value) {
            notifyGenericError();

            return;
        }

        if (shouldShowInspectionExpiredModal.value) {
            if (!(await inspectionExpiredModalState.open())) return;
        }

        isPlaceOrderSubFlow ? await placeOrderSubFlow() : await startNegotiationSubFlow(offerAmount.value);
    } catch {
        // BSD-2821 Loader kept on success to avoid showing the page without loading while navigation is blocked by the destination page's suspense
        isLoadingModalOpen.value = false;
    } finally {
        makeOfferFlowInProgress.value = false;
    }
};

const closeModals = (): void => {
    closeLeadModal();
    inspectionExpiredModalState.cancel();
    bailModalState.cancel();
    isInspectionExportBlockModalOpen.value = false;
};

// WATCHERS
// Sync offerAmount with directSale.price only if the user hasn't touched the stepper.
watch(
    () => directSale.price,
    newPrice => {
        if (!hasUserModifiedOffer.value && newPrice !== undefined) {
            offerAmount.value = newPrice;
        }
    }
);

watch(isLoggedIn, () => {
    // Reset internal state on login change
    closeModals();
});

watch(
    () => directSale,
    value => {
        if (hasOrder.value || !bailModalState.isOpen) return;

        if (value.computedState === DirectSaleComputedState.PendingPayment) {
            closeModals();

            notifyError({
                title: tt('notifications.userDirectSaleAlreadyPurchased.title'),
                description: tt('notifications.userDirectSaleAlreadyPurchased.description', {
                    subject: tt('notifications.userDirectSaleAlreadyPurchased.inDirectSale'),
                }),
                duration: NotificationDuration.NoTimeout,
            });
        }

        if ([DirectSaleState.Closed, DirectSaleState.Canceled].includes(value.state) && makeOfferFlowInProgress.value) {
            closeModals();

            notifyError({
                title: tt(`sdp.DirectSale.order.expiredError.title`),
                description: tt(`sdp.DirectSale.order.expiredError.description`),
                duration: NotificationDuration.NoTimeout,
            });
        }
    }
);
</script>

<template>
    <div v-if="showSection" class="flex flex-col justify-center bg-white p-4 lg:p-6">
        <div class="flex flex-col gap-3">
            <SdpSalePanelNoticeRestricted v-if="hasRestrictedNotice" :sale="directSale" />
            <template v-else>
                <SdpSalePanelNoticeBase
                    v-if="hasInspectionNotice"
                    :message="tt(`sdp.notice.inspectionExpired`)"
                    icon-name="lucide:triangle-alert"
                    icon-class="text-warning-500"
                />

                <template v-if="!isSellerPov">
                    <!--https://adorea.atlassian.net/browse/PT-730?focusedCommentId=49632-->
                    <DsdpPriceRequestModalButton v-if="directSale.priceOnRequest" :direct-sale="directSale" />

                    <UButton
                        v-else-if="!isLoggedIn"
                        size="lg"
                        :ui="buttonResponsiveSizeUi('lg', 'xl')"
                        :label="tt('common.loginSignup')"
                        block
                        :loading="loginFlowInProgress"
                        @click="loginFlow"
                    />

                    <template v-else-if="hasMakeOffer">
                        <DsdpSalePanelMakeOffer
                            v-if="isDirectSaleItemEntityWithPrice(directSale)"
                            v-model="offerAmount"
                            :direct-sale="directSale"
                            @update:model-value="hasUserModifiedOffer = true"
                        />
                        <UButton
                            size="lg"
                            :ui="buttonResponsiveSizeUi('lg', 'xl')"
                            :label="tt('directSaleNegotiation.makeOfferButton')"
                            block
                            :loading="makeOfferFlowInProgress"
                            @click="makeOfferFlow"
                        />
                    </template>
                </template>

                <LazySdpInspectionExpiredModal
                    v-if="shouldShowInspectionExpiredModal"
                    v-model="inspectionExpiredModalState.isOpen"
                    @confirm="inspectionExpiredModalState.confirm(true)"
                />

                <LazyDsdpOrderModal
                    v-if="isDirectSaleItemEntityWithPrice(directSale)"
                    v-model="orderModalState.isOpen"
                    :direct-sale="directSale"
                    :loading="isOrderPending"
                    @confirm="orderModalState.confirm(true)"
                />

                <LazyDsdpBailModal
                    v-if="isBailNeeded && (currentOrderId || currentNegotiationId)"
                    v-model="bailModalState.isOpen"
                    :type="subflowType"
                    :direct-sale="directSale"
                    :order-id="currentOrderId"
                    :negotiation-id="currentNegotiationId"
                    @success="bailModalState.confirm(true)"
                />

                <LazySdpInspectionExportBlockModal
                    v-if="inspectionExportBlockReason"
                    v-model="isInspectionExportBlockModalOpen"
                    :reason="inspectionExportBlockReason"
                />
            </template>

            <LazyDsdpPriceReductionModalButton v-if="hasPriceReduction && isDirectSaleItemEntityWithPrice(directSale)" :direct-sale="directSale" />

            <SdpSalePanelGoToNegotiation v-if="hasGoToNegotiation" :sale="directSale" />
        </div>
    </div>
    <BaseLoadingModal v-model:open="isLoadingModalOpen" :title="loadingModalTitle" />
</template>
